/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Token Cache Service
 *
 * Caches authentication tokens to reduce network overhead.
 * Implements automatic refresh before expiry.
 *
 * Features:
 * - Token caching with automatic expiry
 * - Proactive refresh before token expires
 * - Exponential backoff retry
 * - Memory leak prevention with proper timer cleanup
 * - Visibility-aware refresh (pause when tab hidden)
 */

import axios from "axios";
import configuration from "@/config/configuration";
import { logger } from "@/lib/logger";

interface CachedToken {
  token: string;
  fetchedAt: number;
  expiresIn: number; // milliseconds until expiry
}

interface TokenCacheStats {
  cacheHits: number;
  cacheMisses: number;
  refreshCount: number;
  failedRefreshes: number;
  lastRefreshAt: number | null;
}

class TokenCacheService {
  private cachedToken: CachedToken | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private isPageVisible = true;

  private readonly TOKEN_REFRESH_BUFFER = 60 * 1000; // Refresh 1 minute before expiry
  // Session 545 decision (confirmed by Sunil): 60 minutes for SaaS dashboard.
  // Pre-cutover: used as fallback when Better Auth token endpoint omits expiresIn.
  // Post-cutover: the ds_auth_token exp claim is the authoritative source (read by
  // getStoredAuthToken); this constant is no longer in the critical path.
  private readonly DEFAULT_TOKEN_LIFETIME = 60 * 60 * 1000; // 60 minutes
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second base delay
  private readonly MIN_REFRESH_INTERVAL = 30 * 1000; // Don't refresh more than once per 30s

  private stats: TokenCacheStats = {
    cacheHits: 0,
    cacheMisses: 0,
    refreshCount: 0,
    failedRefreshes: 0,
    lastRefreshAt: null,
  };

  constructor() {
    // Set up visibility change handler to pause/resume refresh
    if (typeof document !== "undefined") {
      this.visibilityHandler = () => {
        this.isPageVisible = document.visibilityState === "visible";
        if (this.isPageVisible && this.cachedToken) {
          // Check if token needs refresh when page becomes visible
          if (!this.isTokenValid()) {
            this.refreshToken().catch(() => {
              // Ignore - will be handled on next API call
            });
          }
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  /**
   * Get a valid token, fetching a new one if necessary
   */
  async getToken(): Promise<string | null> {
    // Issue 2 (FCR Pass 10) Wave A+: Check ds_auth_token (Auth Service JWT) first.
    // Pre-cutover this returns null (no token stored yet) and falls through to the
    // Better Auth network call below -- so today's behaviour is unchanged.
    // Post-cutover the stored JWT is used directly; the Better Auth endpoint is gone.
    const storedJwt = getStoredAuthToken();
    if (storedJwt) {
      logger.debug("Using stored Auth Service JWT from ds_auth_token");
      return storedJwt;
    }

    // Check if we have a valid cached token (from the Better Auth network fetch below)
    if (this.isTokenValid()) {
      this.stats.cacheHits++;
      logger.debug("Using cached token");
      return this.cachedToken?.token ?? null;
    }

    this.stats.cacheMisses++;

    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      logger.debug("Waiting for token refresh in progress");
      return this.refreshPromise;
    }

    // Start a new token fetch (Better Auth network call -- pre-cutover path only)
    return this.refreshToken();
  }

  /**
   * Force refresh the token
   */
  async refreshToken(): Promise<string | null> {
    // Prevent concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Throttle refreshes to prevent hammering the auth server
    const now = Date.now();
    if (
      this.stats.lastRefreshAt &&
      now - this.stats.lastRefreshAt < this.MIN_REFRESH_INTERVAL
    ) {
      logger.debug("Throttling token refresh - too soon since last refresh");
      return this.cachedToken?.token || null;
    }

    this.refreshPromise = this.fetchTokenWithRetry();

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Fetch token with exponential backoff retry
   */
  private async fetchTokenWithRetry(attempt = 1): Promise<string | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await axios.get<{ token: string; expiresIn?: number }>(
        `${configuration.better_auth_url}/api/auth/token`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (response.data.token) {
        this.cachedToken = {
          token: response.data.token,
          fetchedAt: Date.now(),
          expiresIn: response.data.expiresIn || this.DEFAULT_TOKEN_LIFETIME,
        };

        this.stats.refreshCount++;
        this.stats.lastRefreshAt = Date.now();

        logger.debug(
          `Token fetched and cached (expires in ${this.cachedToken.expiresIn}ms)`,
        );

        // Schedule proactive refresh before expiry
        this.scheduleRefresh();

        return response.data.token;
      }

      logger.warn("Token response did not contain a token");
      return null;
    } catch (error) {
      // Check if we should retry
      if (attempt < this.MAX_RETRIES && this.isRetryableError(error)) {
        const delay = this.RETRY_DELAY * Math.pow(2, attempt - 1);
        logger.warn(
          `Token fetch failed (attempt ${attempt}/${this.MAX_RETRIES}), retrying in ${delay}ms`,
        );

        await this.sleep(delay);
        return this.fetchTokenWithRetry(attempt + 1);
      }

      logger.error("Failed to fetch token after all retries:", error);
      this.stats.failedRefreshes++;
      this.clearCache();
      return null;
    }
  }

  /**
   * Check if the cached token is still valid
   */
  private isTokenValid(): boolean {
    if (!this.cachedToken) {
      return false;
    }

    const elapsed = Date.now() - this.cachedToken.fetchedAt;
    const remainingTime = this.cachedToken.expiresIn - elapsed;

    // Consider token invalid if it's close to expiring
    return remainingTime > this.TOKEN_REFRESH_BUFFER;
  }

  /**
   * Schedule automatic token refresh before expiry
   * Properly cleans up previous timer to prevent memory leaks
   */
  private scheduleRefresh(): void {
    // Clear any existing refresh timer
    this.cancelScheduledRefresh();

    if (!this.cachedToken) return;

    const refreshTime = this.cachedToken.expiresIn - this.TOKEN_REFRESH_BUFFER;

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        // Only refresh if page is visible and we still have the same token
        if (this.isPageVisible && this.cachedToken) {
          logger.debug("Proactively refreshing token before expiry");
          this.refreshToken().catch((error) => {
            logger.error("Proactive token refresh failed:", error);
          });
        }
      }, refreshTime);
    }
  }

  /**
   * Cancel any scheduled refresh timer
   */
  private cancelScheduledRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Check if an error is retryable
   */
  private isRetryableError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      // Don't retry on abort
      if (error.code === "ERR_CANCELED") return false;

      // Retry on network errors or 5xx server errors
      if (!error.response) return true; // Network error
      const status = error.response.status;
      return status >= 500 && status < 600;
    }
    return false;
  }

  /**
   * Clear the token cache and cancel any scheduled refresh
   */
  clearCache(): void {
    this.cancelScheduledRefresh();
    this.cachedToken = null;
    logger.debug("Token cache cleared");
  }

  /**
   * Sleep helper for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get time until token expires (for debugging)
   */
  getTimeUntilExpiry(): number | null {
    if (!this.cachedToken) return null;

    const elapsed = Date.now() - this.cachedToken.fetchedAt;
    return Math.max(0, this.cachedToken.expiresIn - elapsed);
  }

  /**
   * Get cache statistics
   */
  getStats(): Readonly<TokenCacheStats> {
    return { ...this.stats };
  }

  /**
   * Check if token is currently cached (without fetching)
   */
  hasToken(): boolean {
    return this.isTokenValid();
  }

  /**
   * Cleanup resources (call on app unmount)
   */
  destroy(): void {
    this.cancelScheduledRefresh();

    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }

    this.cachedToken = null;
    logger.debug("Token cache destroyed");
  }
}

// Export singleton instance
export const tokenCache = new TokenCacheService();

// Export function for backward compatibility
export const fetchSession = (): Promise<string | null> => {
  return tokenCache.getToken();
};

// Export type for testing
export { TokenCacheService };

// ============================================================================
// Issue 2 (FCR Pass 10): JWT localStorage helpers for Auth Service cutover.
// These are additive -- the Better Auth cookie flow is unchanged until Wave C.
// All helpers are no-ops when no JWT is present (pre-cutover safe).
// ============================================================================

const DS_AUTH_TOKEN_KEY = "ds_auth_token";

/**
 * Decode the payload of a JWT token safely.
 * Handles base64url padding and UTF-8 encoding so accented names are not
 * garbled. Returns null on any parse error.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const seg = token.split(".")[1];
    if (!seg) return null;
    // Restore base64url padding
    let b64 = seg.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4 !== 0) b64 += "=";
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Read the stored JWT from localStorage only if it is not expired.
 * An expired token is treated as absent so the 401 interceptor redirects to
 * login rather than re-attaching a dead credential.
 */
export function getStoredAuthToken(): string | null {
  try {
    const token = localStorage.getItem(DS_AUTH_TOKEN_KEY);
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp as number | undefined;
    if (exp && exp * 1000 <= Date.now()) {
      // Token expired -- remove it so the next getToken() triggers a fresh auth
      localStorage.removeItem(DS_AUTH_TOKEN_KEY);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

/** Persist a JWT to localStorage under the canonical key. */
export function storeAuthToken(token: string): void {
  try {
    localStorage.setItem(DS_AUTH_TOKEN_KEY, token);
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

/** Remove the JWT from localStorage (on logout or session expiry). */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(DS_AUTH_TOKEN_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Read ?authToken= from the current URL and persist it to localStorage.
 * Called once on app mount (root.tsx). Safe to call when no param is present.
 * Removes the param from the address bar via replaceState after capture so it
 * does not appear in browser history or get forwarded in copy-paste links.
 */
export function captureAuthTokenFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("authToken");
    if (!token) return;
    storeAuthToken(token);
    // Remove the token from the URL without adding a history entry
    params.delete("authToken");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
    logger.debug("Captured auth token from URL");
  } catch {
    // Ignore -- non-critical path
  }
}
